export const SOURCE_STATE_CLASSES = Object.freeze({
  CONTRACT_REAL: 'contract-real',
  CONTRACT_BACKED: 'contract-backed',
  SOURCE_LOCAL: 'source-local',
  PROTECTED_PREVIEW: 'protected-preview',
  NON_AUTHORED: 'non-authored',
  PROVIDER_LIVE_NOT_TESTED: 'provider-live-not-tested',
  MISSING_WARNING: 'missing-warning',
  SNAPSHOT_STATIC: 'snapshot-static',
  SYNTHETIC_DERIVED: 'synthetic-derived',
  FROZEN_LOCAL_FAKE: 'frozen-local-fake',
  SCENARIO_LOCAL: 'scenario-local',
  COMPUTED_DERIVED: 'computed-derived',
});

export const PROVIDER_HEALTH_MAX_AGE_MS = 15 * 60 * 1000;

export const SOURCE_STATE_META = Object.freeze({
  [SOURCE_STATE_CLASSES.CONTRACT_REAL]: {
    label: 'Contract source',
    compactLabel: 'contract-real',
    defaultTip: 'Ledger API contract data. Strict real-book proof still requires explicit book scope, auth, provider currentness, and no fallback.',
  },
  [SOURCE_STATE_CLASSES.CONTRACT_BACKED]: {
    label: 'Contract backed',
    compactLabel: 'contract-backed',
    defaultTip: 'Ledger contract field is present. Treat strict/public claims separately from contract presence.',
  },
  [SOURCE_STATE_CLASSES.SOURCE_LOCAL]: {
    label: 'Source local',
    compactLabel: 'source-local',
    defaultTip: 'Ledger contract was generated from a local source tier. Useful for development proof, not live/provider proof.',
  },
  [SOURCE_STATE_CLASSES.PROTECTED_PREVIEW]: {
    label: 'Protected preview',
    compactLabel: 'protected-preview',
    defaultTip: 'Ledger contract came from a protected preview/source tier. Do not treat as strict-live or public/client proof.',
  },
  [SOURCE_STATE_CLASSES.NON_AUTHORED]: {
    label: 'Non-authored',
    compactLabel: 'non-authored',
    defaultTip: 'Ledger contract explicitly says this report or row is not authored as strict Ledger line truth.',
  },
  [SOURCE_STATE_CLASSES.PROVIDER_LIVE_NOT_TESTED]: {
    label: 'Provider not tested',
    compactLabel: 'provider-not-tested',
    defaultTip: 'Provider posture is explicitly not live-tested by this contract.',
  },
  [SOURCE_STATE_CLASSES.MISSING_WARNING]: {
    label: 'Missing or warning',
    compactLabel: 'warning',
    defaultTip: 'Ledger contract emitted a warning or missing-evidence state. Treat as held until resolved.',
  },
  [SOURCE_STATE_CLASSES.SNAPSHOT_STATIC]: {
    label: 'Static snapshot',
    compactLabel: 'snapshot-static',
    defaultTip: 'Static bundled snapshot. Useful for local review, not strict current Ledger proof.',
  },
  [SOURCE_STATE_CLASSES.SYNTHETIC_DERIVED]: {
    label: 'Synthetic derived',
    compactLabel: 'synthetic-derived',
    defaultTip: 'Derived from local assumptions or model fallbacks. No posted-book proof.',
  },
  [SOURCE_STATE_CLASSES.FROZEN_LOCAL_FAKE]: {
    label: 'Frozen local fake',
    compactLabel: 'frozen-local-fake',
    defaultTip: 'Frozen fake/local fixture. No provider, no real finance rows, no client/public proof.',
  },
  [SOURCE_STATE_CLASSES.SCENARIO_LOCAL]: {
    label: 'Private browser scenario',
    compactLabel: 'scenario-local',
    defaultTip: 'Private local browser scenario/profile state. Review and redact before sharing.',
  },
  [SOURCE_STATE_CLASSES.COMPUTED_DERIVED]: {
    label: 'Computed derived',
    compactLabel: 'computed-derived',
    defaultTip: 'Computed from one or more labeled inputs. Check source classes before treating as proof.',
  },
});

const LEGACY_SOURCE_KIND_MAP = Object.freeze({
  contract: SOURCE_STATE_CLASSES.CONTRACT_REAL,
  ledger: SOURCE_STATE_CLASSES.CONTRACT_REAL,
  'contract-backed': SOURCE_STATE_CLASSES.CONTRACT_BACKED,
  'source-local': SOURCE_STATE_CLASSES.SOURCE_LOCAL,
  'protected-preview': SOURCE_STATE_CLASSES.PROTECTED_PREVIEW,
  'non-authored': SOURCE_STATE_CLASSES.NON_AUTHORED,
  'non-strict': SOURCE_STATE_CLASSES.NON_AUTHORED,
  'provider-live-not-tested': SOURCE_STATE_CLASSES.PROVIDER_LIVE_NOT_TESTED,
  'provider-not-tested': SOURCE_STATE_CLASSES.PROVIDER_LIVE_NOT_TESTED,
  missing: SOURCE_STATE_CLASSES.MISSING_WARNING,
  warning: SOURCE_STATE_CLASSES.MISSING_WARNING,
  snapshot: SOURCE_STATE_CLASSES.SNAPSHOT_STATIC,
  schedule: SOURCE_STATE_CLASSES.SCENARIO_LOCAL,
  forecast: SOURCE_STATE_CLASSES.SYNTHETIC_DERIVED,
  synthetic: SOURCE_STATE_CLASSES.SYNTHETIC_DERIVED,
  computed: SOURCE_STATE_CLASSES.COMPUTED_DERIVED,
  'ledger-commercial-timing': SOURCE_STATE_CLASSES.COMPUTED_DERIVED,
  local: SOURCE_STATE_CLASSES.SCENARIO_LOCAL,
  frozen: SOURCE_STATE_CLASSES.FROZEN_LOCAL_FAKE,
});

const STRICT_PROOF_TIERS = new Set([
  'strict-book-scoped',
  'provider-verified',
  'handoff-accepted',
]);

export function normalizeProviderHealth(input, { now = Date.now() } = {}) {
  if (!input || typeof input !== 'object') {
    return {
      liveProof: false,
      holdReason: 'provider-health route not tested',
    };
  }

  const checkedAtMs = Date.parse(input.checkedAt || '');
  if (!Number.isFinite(checkedAtMs)) {
    return {
      ...input,
      liveProof: false,
      holdReason: 'provider-health checkedAt missing or invalid',
    };
  }

  const ageMs = Math.max(0, now - checkedAtMs);
  const explicitHoldReason = typeof input.holdReason === 'string' && input.holdReason.trim()
    ? input.holdReason.trim()
    : null;
  const base = {
    provider: input.provider || 'currencybeacon',
    moneyRateOracle: input.moneyRateOracle || 'provider-neutral-money-rate',
    providerCoverageClass: input.providerCoverageClass || input.activeProviderCoverageClass || 'commercial-multi-asset',
    fiatReferenceLane: input.fiatReferenceLane || 'commercial-adapter-not-official-reference',
    cryptoPricingLane: input.cryptoPricingLane || 'commercial-multi-asset-adapter',
    liveStatus: input.liveStatus || 'unavailable',
    checkedAt: input.checkedAt,
    samplePair: input.samplePair || 'USD/EUR',
    sampleMoneyClass: input.sampleMoneyClass || 'fiat-fx',
    quotaCostClass: input.quotaCostClass || 'single-pair-low',
    secretExposed: input.secretExposed === true,
    ageMs,
  };

  if (base.secretExposed) {
    return { ...base, liveProof: false, holdReason: explicitHoldReason || 'provider-health response exposed a secret flag' };
  }
  if (base.liveStatus !== 'available') {
    return { ...base, liveProof: false, holdReason: explicitHoldReason || `provider-health liveStatus=${base.liveStatus}` };
  }
  if (ageMs > PROVIDER_HEALTH_MAX_AGE_MS) {
    return { ...base, liveProof: false, holdReason: explicitHoldReason || 'provider-health check is stale' };
  }

  return {
    ...base,
    liveProof: true,
    holdReason: null,
  };
}

const DATA_TIER_CLASS_MAP = Object.freeze({
  'source-local': SOURCE_STATE_CLASSES.SOURCE_LOCAL,
  'protected-preview': SOURCE_STATE_CLASSES.PROTECTED_PREVIEW,
  'strict-live': SOURCE_STATE_CLASSES.CONTRACT_REAL,
  'provider-live-db': SOURCE_STATE_CLASSES.CONTRACT_REAL,
  'real-book': SOURCE_STATE_CLASSES.CONTRACT_REAL,
});

const REPORT_TYPE_ALIASES = Object.freeze({
  'trial-balance': 'trial-balance',
  trialbalance: 'trial-balance',
  'income-statement': 'income-statement',
  incomestatement: 'income-statement',
  income: 'income-statement',
  'balance-sheet': 'balance-sheet',
  balancesheet: 'balance-sheet',
  'financial-position': 'balance-sheet',
  financialposition: 'balance-sheet',
  'cash-flow': 'cash-flow',
  cashflow: 'cash-flow',
  'general-ledger': 'general-ledger',
  generalledger: 'general-ledger',
  'equity-movement': 'equity-movement',
  equitymovement: 'equity-movement',
  socie: 'equity-movement',
});

const FORMAL_FINANCIAL_STATEMENT_REPORT_TYPES = new Set([
  'income-statement',
  'balance-sheet',
  'cash-flow',
  'equity-movement',
]);

export function uniqueWarningCodes(...sources) {
  const codes = [];
  const add = (value) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(add);
      return;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) codes.push(trimmed);
      return;
    }
    if (typeof value === 'object') {
      add(value.warningCode);
      add(value.warningCodes);
      add(value.warnings);
    }
  };
  sources.forEach(add);
  return [...new Set(codes)];
}

export function sourceClassForDataTier(dataTier) {
  const raw = String(dataTier || '').trim();
  return DATA_TIER_CLASS_MAP[raw] || null;
}

export function resolveLedgerReportType(input) {
  const raw = String(input?.reportType || input?.title || input?.label || input || '')
    .trim()
    .toLowerCase();
  if (!raw) return null;
  const key = raw.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const compact = key.replace(/-/g, '');
  if (REPORT_TYPE_ALIASES[key]) return REPORT_TYPE_ALIASES[key];
  if (REPORT_TYPE_ALIASES[compact]) return REPORT_TYPE_ALIASES[compact];
  if (raw.includes('trial balance')) return 'trial-balance';
  if (raw.includes('income')) return 'income-statement';
  if (raw.includes('financial position') || raw.includes('balance sheet')) return 'balance-sheet';
  if (raw.includes('cash flow')) return 'cash-flow';
  if (raw.includes('general ledger')) return 'general-ledger';
  if (raw.includes('equity') || raw.includes('changes in equity')) return 'equity-movement';
  return null;
}

export function findAuthorshipReport(reportLineAuthorship, reportType) {
  if (!reportLineAuthorship || !reportType) return null;
  return (reportLineAuthorship.reports || []).find((report) => report.reportType === reportType) || null;
}

export function findReportProofReport(reportProof, reportType) {
  if (!reportProof || !reportType) return null;
  return (reportProof.reports || []).find((report) => report.reportType === reportType) || null;
}

function reportHasAggregateRows(proofReport, authorshipReport) {
  return Boolean(
    proofReport?.lineProvenance?.emissionScope === 'ledger-service-aggregate-rows' ||
    proofReport?.lineProvenance?.status === 'aggregate-rows-available-via-report-line-authorship' ||
    proofReport?.capability?.aggregateRowsAvailable === true ||
    proofReport?.capability?.statementAggregateRowsAvailable === true ||
    authorshipReport?.proofPosture?.rowAuthorship === 'ledger-service-derived-journal-line-aggregate' ||
    authorshipReport?.proofPosture?.rowAuthorship === 'ledger-service-calculated-journal-line-aggregate'
  );
}

function reportEmissionScopeFor(proofReport, authorshipReport) {
  if (proofReport?.lineProvenance?.emissionScope) return proofReport.lineProvenance.emissionScope;
  if (proofReport?.reportType === 'general-ledger') return 'posted-journal-line-atoms';
  if (reportHasAggregateRows(proofReport, authorshipReport)) return 'ledger-service-aggregate-rows';
  if (proofReport?.capability?.lineItemsAvailable === true) return 'line-items-available-legacy';
  return null;
}

function formalStatementLineItemsFullyEmitted(proofReport, reportType) {
  const contractValue = proofReport?.lineProvenance?.formalStatementLineItemsFullyEmitted
    ?? proofReport?.capability?.formalStatementLineItemsFullyEmitted;
  if (contractValue === true || contractValue === false) return contractValue;
  if (FORMAL_FINANCIAL_STATEMENT_REPORT_TYPES.has(reportType)) return false;
  return null;
}

export function sourceClassForMoneyProvenance(moneyProvenance) {
  if (!moneyProvenance) return null;
  const warnings = uniqueWarningCodes(moneyProvenance);
  if (
    moneyProvenance.providerMode === 'missing' ||
    moneyProvenance.rateSource === 'missing' ||
    moneyProvenance.rateResolution === 'MISSING' ||
    warnings.some((code) => /missing|warning|not-emitted|not-hydrated|review|required/i.test(code))
  ) {
    return SOURCE_STATE_CLASSES.MISSING_WARNING;
  }
  if (moneyProvenance.providerMode === 'system_derived_protected_preview') {
    return SOURCE_STATE_CLASSES.PROTECTED_PREVIEW;
  }
  return SOURCE_STATE_CLASSES.CONTRACT_BACKED;
}

export function formatMoneyProvenanceTooltip(moneyProvenance) {
  if (!moneyProvenance) return SOURCE_STATE_META[SOURCE_STATE_CLASSES.NON_AUTHORED].defaultTip;
  const warnings = uniqueWarningCodes(moneyProvenance);
  const providerPosture = moneyProvenance.providerPosture || null;
  const moneyRateOracle = providerPosture?.moneyRateOracle || null;
  const currencyBeacon = providerPosture?.currencyBeacon || null;
  const providerMode = moneyProvenance.providerMode || moneyRateOracle?.evidenceMode || currencyBeacon?.providerMode;
  const rateSource = moneyProvenance.sourceProvider || moneyProvenance.rateSource || providerMode || 'contract';
  const rateDate = moneyProvenance.rateDate || moneyProvenance.sourceDateUsed || null;
  const basis = moneyProvenance.currencyBasis || null;
  const origin = moneyProvenance.baseCurrency || moneyProvenance.originCurrency || basis?.bookDefaultCurrency || null;
  const target = moneyProvenance.quoteCurrency || moneyProvenance.bookDefaultCurrency || basis?.displayCurrency || basis?.reportingCurrency || null;
  const parts = [
    origin && target ? `Money provenance: ${origin}->${target}.` : 'Money provenance contract present.',
    moneyProvenance.moneyClass ? `Money class: ${moneyProvenance.moneyClass}.` : null,
    `Rate source: ${rateSource}.`,
  ].filter(Boolean);
  if (moneyProvenance.rate || moneyProvenance.exchangeRate) parts.push(`Rate: ${moneyProvenance.rate || moneyProvenance.exchangeRate}.`);
  if (rateDate) parts.push(`Rate date: ${rateDate}.`);
  if (moneyProvenance.fetchedAt) parts.push(`Fetched at: ${moneyProvenance.fetchedAt}.`);
  if (moneyProvenance.fallbackReason) parts.push(`Fallback: ${moneyProvenance.fallbackReason}.`);
  if (moneyProvenance.reviewReason) parts.push(`Review: ${moneyProvenance.reviewReason}.`);
  if (moneyRateOracle) {
    parts.push(`Oracle: ${moneyRateOracle.oracle}; active provider ${moneyRateOracle.activeProvider}.`);
  }
  if (moneyProvenance.bookDefaultAmount) parts.push('Stored book-default amount is preserved; Lens does not recompute it.');
  if (warnings.length > 0) parts.push(`Warnings: ${warnings.join(', ')}.`);
  return parts.join(' ');
}

function displayContractValue(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text || null;
}

function boolLabel(value, trueLabel, falseLabel, unknownLabel) {
  if (value === true) return trueLabel;
  if (value === false) return falseLabel;
  return unknownLabel;
}

function owns(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

export function getLedgerBookSettingsItems(input) {
  const state = normalizeSourceState(input);
  const settings = state.bookSettings || state.meta?.bookSettings || null;
  if (!settings) return [];

  const defaultCurrency = displayContractValue(settings.defaultCurrency);
  const reportingCurrency = displayContractValue(settings.reportingCurrency);
  const currency = defaultCurrency && reportingCurrency && defaultCurrency !== reportingCurrency
    ? `${defaultCurrency}->${reportingCurrency}`
    : reportingCurrency || defaultCurrency;

  return [
    { key: 'currency', label: 'Currency', value: currency },
    { key: 'accounting', label: 'Accounting', value: displayContractValue(settings.accountingStandard) },
    { key: 'reporting', label: 'Reporting', value: displayContractValue(settings.reportingStandard) },
    { key: 'tax', label: 'Tax', value: displayContractValue(settings.taxJurisdictionStandard) },
    { key: 'timezone', label: 'Timezone', value: displayContractValue(settings.timezone) },
    { key: 'fiscalYearEnd', label: 'FY end', value: displayContractValue(settings.fiscalYearEnd) },
  ].filter((item) => item.value);
}

export function hasLedgerBookSettings(input) {
  return getLedgerBookSettingsItems(input).length > 0;
}

export function formatLedgerBookSettingsSummary(input, { compact = false } = {}) {
  const items = getLedgerBookSettingsItems(input);
  if (items.length === 0) return compact ? 'book settings pending' : 'Book settings unavailable';
  const byKey = Object.fromEntries(items.map((item) => [item.key, item.value]));

  if (compact) {
    const standards = [byKey.accounting, byKey.reporting, byKey.tax].filter(Boolean).join('/');
    return [
      byKey.currency ? `Cur ${byKey.currency}` : null,
      standards || null,
      byKey.timezone ? `TZ ${byKey.timezone}` : null,
    ].filter(Boolean).join(' · ');
  }

  return items.map((item) => `${item.label}: ${item.value}`).join(' · ');
}

export function formatLedgerBookSettingsTooltip(input) {
  const items = getLedgerBookSettingsItems(input);
  if (items.length === 0) {
    return 'No Ledger-provided bookSettings are present in Lens source state yet.';
  }
  return [
    `Ledger-provided Book metadata: ${items.map((item) => `${item.label} ${item.value}`).join('; ')}.`,
    'Metadata only: this does not prove Lens statement rows are Ledger-authored or strict-live correct.',
  ].join(' ');
}

export function getBookScopePosture(input) {
  const state = normalizeSourceState(input);
  const scope = state.bookScope || state.meta?.bookScope || null;
  const explicitBook = scope?.explicitBook ?? state.hasExplicitBook ?? null;
  const autoPickedBook = scope?.autoPickedBook ?? state.autoPickedBook ?? null;
  const hasActiveBook = state.hasActiveBook ?? Boolean(scope?.bookId);
  const hasActiveScope = state.hasActiveScope ?? Boolean(scope?.bookId || scope?.shelfId || hasActiveBook);
  const noActiveScopeHold = hasActiveScope === false && state.fallbackTier === 'scope-hold';
  const tenantMembershipVerified = scope?.tenantMembershipVerified ?? null;
  const currentness = state.contractCurrentness || state.currentness || null;
  const sourceFallbackReason = state.fallbackReason || currentness?.fallbackReason || null;
  const currentnessNoFallback = Boolean(currentness?.noFallback === true && !sourceFallbackReason);
  const scopeLabel = noActiveScopeHold
    ? 'no Book/Shelf scope'
    : autoPickedBook === true
    ? 'auto-picked Book'
    : explicitBook === true
      ? 'explicit Book'
      : hasActiveBook
        ? 'active Book'
        : 'book pending';
  const scopeShort = noActiveScopeHold
    ? 'no-scope'
    : autoPickedBook === true
    ? 'auto-picked'
    : explicitBook === true
      ? 'explicit'
      : hasActiveBook
        ? 'active'
        : 'pending';
  const membershipLabel = boolLabel(
    tenantMembershipVerified,
    'membership verified',
    'membership unverified',
    'membership unknown',
  );
  const membershipShort = boolLabel(tenantMembershipVerified, 'verified', 'unverified', 'unknown');
  const currentnessLabel = currentness
    ? currentnessNoFallback
      ? 'no fallback'
      : 'fallback HOLD'
    : 'currentness pending';
  const currentnessShort = currentness
    ? currentnessNoFallback
      ? 'no-fallback'
      : 'fallback-HOLD'
    : 'currentness-pending';
  const providerLabel = state.providerLiveProof
    ? 'provider live'
    : state.sourceClass === SOURCE_STATE_CLASSES.CONTRACT_REAL
      ? 'provider HOLD'
      : 'provider n/a';
  const strictAvailable = state.canClaimStrictRealBook === true;
  const scopeCanClaim = scope?.canClaimStrictRealBook === true;
  const strictLabel = strictAvailable ? 'strict available' : 'strict HOLD';
  const strictNote = strictAvailable
    ? 'Strict real-book claim is available inside this private contract source-state posture.'
    : scopeCanClaim
      ? 'Book scope itself is strict-ready, but source currentness/fallback posture still holds the full strict claim.'
      : 'Strict real-book claim is held by the source-state contract.';

  return {
    hasSignal: Boolean(state.hasBookScopeSignal ?? (scope || hasActiveBook || explicitBook !== null || autoPickedBook !== null)),
    scope,
    basis: displayContractValue(scope?.basis),
    explicitBook,
    autoPickedBook,
    hasActiveScope,
    tenantMembershipVerified,
    currentness,
    currentnessNoFallback,
    currentnessLabel,
    currentnessShort,
    proofTier: currentness?.proofTier || null,
    sourceFallbackReason,
    providerLabel,
    canClaimStrictRealBook: strictAvailable,
    scopeCanClaimStrictRealBook: scopeCanClaim,
    scopeLabel,
    scopeShort,
    membershipLabel,
    membershipShort,
    strictLabel,
    strictNote,
  };
}

export function hasBookScopePosture(input) {
  return getBookScopePosture(input).hasSignal;
}

export function formatBookScopePostureLabel(input, { compact = false } = {}) {
  const posture = getBookScopePosture(input);
  if (!posture.hasSignal) return compact ? 'scope pending · strict HOLD' : 'Book scope pending · strict HOLD';
  if (compact) {
    return `${posture.scopeShort} · ${posture.membershipShort} · ${posture.currentnessShort} · ${posture.strictLabel}`;
  }
  return `${posture.scopeLabel} · ${posture.membershipLabel} · ${posture.currentnessLabel} · ${posture.strictLabel}`;
}

export function formatBookScopePostureTooltip(input) {
  const posture = getBookScopePosture(input);
  const parts = [
    'Book scope posture from Ledger source state.',
    `Scope: ${posture.scopeLabel}.`,
    `Membership: ${posture.membershipLabel}.`,
    `Currentness: ${posture.currentnessLabel}.`,
    `Strict real-book claim: ${posture.strictLabel}.`,
    posture.strictNote,
  ];
  if (posture.basis) parts.push(`Basis: ${posture.basis}.`);
  if (posture.proofTier) parts.push(`Proof tier: ${posture.proofTier}.`);
  if (posture.sourceFallbackReason) parts.push(`Fallback reason: ${posture.sourceFallbackReason}.`);
  parts.push(`Provider: ${posture.providerLabel}.`);
  if (posture.autoPickedBook === true) parts.push('Auto-picked Book remains a heuristic and cannot carry strict real-book proof.');
  parts.push('Book scope metadata does not make non-GL statement rows Ledger-authored.');
  return parts.filter(Boolean).join(' ');
}

export function buildLedgerContractExplanation(input = {}) {
  const sourceState = normalizeSourceState(input.sourceState || input);
  const reportType = input.reportType || resolveLedgerReportType(input);
  const reportLineAuthorship = input.reportLineAuthorship || sourceState.reportLineAuthorship || null;
  const reportProof = input.reportProof || sourceState.reportProof || null;
  const moneyProvenance = input.moneyProvenance || sourceState.moneyProvenance || null;
  const authorshipReport = findAuthorshipReport(reportLineAuthorship, reportType);
  const proofReport = findReportProofReport(reportProof, reportType);
  const dataTier = reportLineAuthorship?.dataTier || moneyProvenance?.dataTier || reportProof?.dataTier || sourceState.dataTier || null;
  const dataTierClass = sourceClassForDataTier(dataTier);
  const reportWarnings = uniqueWarningCodes(authorshipReport, proofReport);
  const contractWarnings = uniqueWarningCodes(sourceState.contractWarningCodes, reportLineAuthorship, moneyProvenance);
  const warningCodes = uniqueWarningCodes(reportWarnings, contractWarnings);
  const authoredByLedger = Boolean(authorshipReport?.emitted && authorshipReport?.proofPosture?.authoredByLedger);
  const aggregateRowsAvailable = reportHasAggregateRows(proofReport, authorshipReport);
  const reportEmissionScope = reportEmissionScopeFor(proofReport, authorshipReport);
  const formalLineItemsFullyEmitted = formalStatementLineItemsFullyEmitted(proofReport, reportType);
  const formalStatementLineItemsHeld = formalLineItemsFullyEmitted === false;
  const formalStatementLineItemHeldReason = formalStatementLineItemsHeld
    ? 'formal-statement-line-items-not-fully-emitted-in-ledger-json-v1'
    : null;
  const strictLedgerAuthoredLineItems = Boolean(
    authoredByLedger &&
    authorshipReport?.proofPosture?.strictLedgerAuthoredLineItems === true &&
    !authorshipReport?.proofPosture?.strictClaimHeldReason &&
    sourceState.canClaimStrictRealBook === true
  );
  const reportClass = authorshipReport
    ? strictLedgerAuthoredLineItems
      ? SOURCE_STATE_CLASSES.CONTRACT_BACKED
      : SOURCE_STATE_CLASSES.NON_AUTHORED
    : null;
  const strictReportHeldReason = authorshipReport?.proofPosture?.strictClaimHeldReason
    || proofReport?.lineProvenance?.basis
    || null;
  const moneyRateOracle = moneyProvenance?.providerPosture?.moneyRateOracle || null;
  const currencyBeacon = moneyProvenance?.providerPosture?.currencyBeacon || null;
  const providerPosture = moneyRateOracle || currencyBeacon;
  const providerLiveProof = sourceState.providerLiveProof === true;
  const providerLiveNotTested = Boolean(
    !providerLiveProof &&
    (
      providerPosture?.liveStatus === 'not-tested' ||
      moneyRateOracle?.warningCode === 'money-rate-provider-not-tested-by-ledger-json' ||
      currencyBeacon?.warningCode === 'currencybeacon-live-provider-not-tested-by-ledger-json'
    )
  );
  return {
    sourceState,
    reportType,
    reportLineAuthorship,
    reportProof,
    authorshipReport,
    proofReport,
    moneyProvenance,
    dataTier,
    dataTierClass,
    reportClass,
    reportWarnings,
    contractWarnings,
    warningCodes,
    authoredByLedger,
    aggregateRowsAvailable,
    reportEmissionScope,
    formalStatementLineItemsFullyEmitted: formalLineItemsFullyEmitted,
    formalStatementLineItemsHeld,
    formalStatementLineItemHeldReason,
    strictLedgerAuthoredLineItems,
    canClaimStrictReportRows: strictLedgerAuthoredLineItems,
    strictReportHeldReason,
    moneyRateOracle,
    currencyBeacon,
    providerHealth: sourceState.providerHealth || null,
    providerLiveProof,
    providerHoldReason: sourceState.providerHoldReason || null,
    providerLiveNotTested,
    moneyClass: sourceClassForMoneyProvenance(moneyProvenance),
    hasContractSignal: Boolean(reportLineAuthorship || reportProof || moneyProvenance),
  };
}

function normalizeLookupText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function compactLookupText(value) {
  return normalizeLookupText(value).replace(/\s+/g, '');
}

function uniqueLookupValues(values) {
  return [...new Set(values.map(normalizeLookupText).filter(Boolean))];
}

function aggregateRowLookupValues(row = {}) {
  const idTail = typeof row.id === 'string' ? row.id.split(':').pop() : null;
  return uniqueLookupValues([
    row.label,
    row.name,
    row.accountName,
    row.account?.name,
    row.account?.displayName,
    row.account?.code,
    row.account?.naturalKey,
    row.account?.id,
    idTail,
  ]);
}

function statementLineLookupValues(line = {}) {
  return uniqueLookupValues([
    line.label,
    line.name,
    line.accountName,
    line.account?.name,
    line.account?.displayName,
    line.account?.code,
    line.account?.naturalKey,
    line.account?.id,
  ]);
}

function lookupValuesMatch(left, right) {
  if (!left || !right) return false;
  if (left === right) return true;
  const compactLeft = compactLookupText(left);
  const compactRight = compactLookupText(right);
  if (!compactLeft || !compactRight) return false;
  if (compactLeft === compactRight) return true;
  if (compactLeft.length >= 10 && compactRight.length >= 10) {
    return compactLeft.includes(compactRight) || compactRight.includes(compactLeft);
  }
  return false;
}

export function findLedgerAggregateRowForStatementLine(line, aggregateRows = []) {
  if (!line || !Array.isArray(aggregateRows) || aggregateRows.length === 0) return null;
  const lineLookups = statementLineLookupValues(line);
  if (lineLookups.length === 0) return null;
  return aggregateRows.find((row) => {
    const rowLookups = aggregateRowLookupValues(row);
    return rowLookups.some((rowLookup) => lineLookups.some((lineLookup) => lookupValuesMatch(rowLookup, lineLookup)));
  }) || null;
}

export function decorateStatementSectionsWithAggregateRowProof(sections, input = {}) {
  const explanation = buildLedgerContractExplanation(input);
  const aggregateRows = explanation.aggregateRowsAvailable === true && Array.isArray(explanation.authorshipReport?.lines)
    ? explanation.authorshipReport.lines
    : [];
  const enabled = input.enableAggregateRowProof === true;
  if (!Array.isArray(sections) || sections.length === 0 || aggregateRows.length === 0 || !enabled) {
    return {
      sections,
      explanation,
      aggregateRowsAvailable: aggregateRows.length,
      aggregateRowsMatched: 0,
      aggregateRowProofEnabled: enabled,
    };
  }

  let aggregateRowsMatched = 0;
  const decorateLine = (line) => {
    const aggregateRow = findLedgerAggregateRowForStatementLine(line, aggregateRows);
    if (!aggregateRow) return line;
    aggregateRowsMatched += 1;
    return {
      ...line,
      __ledgerAggregateRow: aggregateRow,
      __ledgerAggregateReportType: explanation.reportType,
      __ledgerAggregateReportLabel: explanation.authorshipReport?.label || explanation.reportType,
      __ledgerAggregateFormalLineItemsHeld: explanation.formalStatementLineItemsHeld,
      __ledgerAggregateFormalLineItemHeldReason: explanation.formalStatementLineItemHeldReason,
    };
  };

  const decoratedSections = sections.map((section) => {
    if (!section || typeof section !== 'object') return section;
    const lines = Array.isArray(section.lines) ? section.lines.map(decorateLine) : section.lines;
    const total = section.total ? decorateLine(section.total) : section.total;
    return { ...section, lines, total };
  });

  return {
    sections: decoratedSections,
    explanation,
    aggregateRowsAvailable: aggregateRows.length,
    aggregateRowsMatched,
    aggregateRowProofEnabled: enabled,
  };
}

export function normalizeSourceClass(input) {
  const raw = String(input || '').trim();
  if (!raw) return SOURCE_STATE_CLASSES.SCENARIO_LOCAL;
  if (SOURCE_STATE_META[raw]) return raw;
  return LEGACY_SOURCE_KIND_MAP[raw] || SOURCE_STATE_CLASSES.SCENARIO_LOCAL;
}

function ledgerContractAllowsStrictClaim(raw, sourceClass) {
  const currentness = raw.contractCurrentness || raw.currentness || raw.meta?.currentness || null;
  const bookScope = raw.bookScope || raw.meta?.bookScope || null;
  if (!currentness || !bookScope) return false;
  return Boolean(
    sourceClass === SOURCE_STATE_CLASSES.CONTRACT_REAL &&
    currentness.noFallback === true &&
    !currentness.fallbackReason &&
    STRICT_PROOF_TIERS.has(currentness.proofTier) &&
    bookScope.canClaimStrictRealBook === true &&
    bookScope.tenantMembershipVerified === true &&
    bookScope.explicitBook === true &&
    bookScope.autoPickedBook !== true
  );
}

export function normalizeSourceState(input, overrides = {}) {
  const raw = typeof input === 'object' && input !== null
    ? { ...input, ...overrides }
    : { kind: input, ...overrides };
  const sourceClass = normalizeSourceClass(raw.sourceClass || raw.kind || raw.source);
  const meta = SOURCE_STATE_META[sourceClass] || SOURCE_STATE_META[SOURCE_STATE_CLASSES.SCENARIO_LOCAL];
  const contractAllowsStrictClaim = ledgerContractAllowsStrictClaim(raw, sourceClass);
  const providerHealthOptions = Number.isFinite(raw.providerHealthNow)
    ? { now: raw.providerHealthNow }
    : {};
  const providerHealth = normalizeProviderHealth(
    raw.providerHealth || raw.meta?.providerHealth || null,
    providerHealthOptions,
  );
  const contractCurrentness = raw.contractCurrentness || raw.currentness || raw.meta?.currentness || null;
  const bookScope = raw.bookScope || raw.meta?.bookScope || null;
  const bookSettings = raw.bookSettings || raw.meta?.bookSettings || null;
  const reportProof = raw.reportProof || raw.meta?.reportProof || null;
  const reportLineAuthorship = raw.reportLineAuthorship || raw.meta?.reportLineAuthorship || null;
  const moneyProvenance = raw.moneyProvenance || raw.meta?.moneyProvenance || null;
  const hasExplicitBook = raw.hasExplicitBook ?? bookScope?.explicitBook ?? false;
  const hasActiveBook = raw.hasActiveBook ?? Boolean(bookScope?.bookId);
  const hasActiveScope = raw.hasActiveScope ?? Boolean(bookScope?.bookId || bookScope?.shelfId || hasActiveBook);
  const autoPickedBook = raw.autoPickedBook ?? bookScope?.autoPickedBook ?? false;
  const hasBookScopeSignal = Boolean(
    bookScope ||
    owns(raw, 'hasExplicitBook') ||
    owns(raw, 'hasActiveBook') ||
    owns(raw, 'hasActiveScope') ||
    owns(raw, 'autoPickedBook')
  );
  const contractWarningCodes = uniqueWarningCodes(
    raw.contractWarningCodes,
    contractCurrentness,
    reportProof,
    reportLineAuthorship,
    moneyProvenance,
  );
  const hasLedgerReportProof = Boolean(
    sourceClass === SOURCE_STATE_CLASSES.CONTRACT_REAL &&
    reportProof?.contractVersion === 'ledger-report-proof-v1'
  );
  const hasLedgerReportLineAuthorship = Boolean(
    sourceClass === SOURCE_STATE_CLASSES.CONTRACT_REAL &&
    reportLineAuthorship?.contractVersion === 'ledger-report-line-authorship-v1'
  );
  const hasMoneyProvenanceContract = Boolean(
    sourceClass === SOURCE_STATE_CLASSES.CONTRACT_REAL &&
    moneyProvenance?.contractVersion === 'ledger-money-provenance-currencybeacon-v1'
  );
  const moneyRateOracle = moneyProvenance?.providerPosture?.moneyRateOracle || null;
  const currencyBeacon = moneyProvenance?.providerPosture?.currencyBeacon || null;
  const providerLiveProof = Boolean(
    sourceClass === SOURCE_STATE_CLASSES.CONTRACT_REAL &&
    providerHealth.liveProof === true
  );
  const canClaimStrictReportProof = Boolean(
    contractAllowsStrictClaim &&
    hasLedgerReportProof &&
    reportProof?.sourceLineage?.noFallback === true &&
    reportProof?.capabilityFlags?.strictReportTruthRequiresLedgerProof === true &&
    reportProof?.bookScope?.bookId
  );
  const canClaimStrictRealBook = Boolean(
    contractAllowsStrictClaim &&
    sourceClass === SOURCE_STATE_CLASSES.CONTRACT_REAL &&
    providerLiveProof &&
    !autoPickedBook &&
    !raw.fallbackReason
  );
  const label = raw.label || raw.sourceLabel || meta.label;
  return {
    ...raw,
    sourceClass,
    label,
    sourceLabel: label,
    compactLabel: raw.compactLabel || meta.compactLabel,
    tooltip: raw.tooltip || raw.warning || meta.defaultTip,
    contractCurrentness,
    bookScope,
    bookSettings,
    hasExplicitBook,
    hasActiveBook,
    hasActiveScope,
    autoPickedBook,
    hasBookScopeSignal,
    reportProof,
    reportLineAuthorship,
    moneyProvenance,
    providerHealth,
    providerLiveProof,
    providerHoldReason: providerHealth.holdReason,
    contractWarningCodes,
    canClaimStrictRealBook,
    hasLedgerReportProof,
    hasLedgerReportLineAuthorship,
    hasMoneyProvenanceContract,
    moneyRateProviderLiveNotTested: Boolean(
      moneyRateOracle?.liveStatus === 'not-tested' ||
      moneyRateOracle?.warningCode === 'money-rate-provider-not-tested-by-ledger-json'
    ),
    currencyBeaconProviderLiveNotTested: Boolean(
      currencyBeacon?.liveStatus === 'not-tested'
    ),
    canClaimStrictReportProof,
  };
}

export function formatSourceStateLabel(input, { compact = false } = {}) {
  const state = normalizeSourceState(input);
  return compact ? state.compactLabel : state.label;
}

export function formatSourceStateTooltip(input) {
  const state = normalizeSourceState(input);
  const parts = [state.tooltip];
  if (state.autoPickedBook) {
    parts.push('Book scope was auto-picked by first-book heuristic; not strict real-book proof.');
  } else if (state.hasExplicitBook) {
    parts.push('Book scope is explicit in configuration.');
  }
  if (state.fallbackReason) parts.push(`Fallback note: ${state.fallbackReason}`);
  if (state.dataTier) parts.push(`Data tier: ${state.dataTier}.`);
  if (state.hasLedgerReportLineAuthorship) parts.push('Report line authorship contract is present.');
  if (state.hasMoneyProvenanceContract) parts.push('Money provenance contract is present.');
  if (state.providerLiveProof) {
    parts.push(`Money-rate provider proof checked at ${state.providerHealth.checkedAt} using ${state.providerHealth.samplePair}; /api/ledger.json remains non-calling provenance.`);
    parts.push('Fiat FX and crypto pricing remain separate provenance classes.');
  } else if (state.sourceClass === SOURCE_STATE_CLASSES.CONTRACT_REAL) {
    parts.push(`Money-rate provider proof HOLD: ${state.providerHoldReason || 'provider-health route not tested'}. Cache-only money-rate provenance is not live provider proof.`);
  }
  if (state.moneyRateProviderLiveNotTested && !state.providerLiveProof) parts.push('Money-rate live provider is explicitly not tested by ledger.json.');
  if (state.currencyBeaconProviderLiveNotTested && !state.providerLiveProof) parts.push('Legacy CurrencyBeacon provider posture is preserved for backward compatibility.');
  if (state.contractWarningCodes?.length) parts.push(`Contract warnings: ${state.contractWarningCodes.join(', ')}.`);
  if (state.sourceClass === SOURCE_STATE_CLASSES.CONTRACT_REAL && !state.canClaimStrictReportProof) {
    parts.push('Strict report claims require Ledger-provided report proof.');
  }
  if (!state.canClaimStrictRealBook) parts.push('Public/client/strict real-book claims remain HOLD.');
  return parts.filter(Boolean).join(' ');
}

export function sanitizeStatementEntity(entity) {
  const value = String(entity || '').trim();
  if (!value) return value;
  if (/jl patawaran|personal/i.test(value)) return 'Restricted source book';
  return value;
}

export function sanitizeStatementSubtitle(subtitle) {
  let value = String(subtitle || '').trim();
  if (!value) return value;
  value = value.replace(/synthetic\s+until\s+zb\s+(historical\s+)?sync( wires up)?/gi, 'synthetic-derived until contract source is verified');
  value = value.replace(/synthetic past until zb sync/gi, 'synthetic-derived past until contract source is verified');
  value = value.replace(/\bZB\b/g, 'Ledger source');
  value = value.replace(/Zoho\s*Books/gi, 'source system');
  return value;
}
